import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Leaf, MapPin, Sprout, Loader2, Eye } from "lucide-react";
import { fetchPublicGarden, formatApiError } from "@/lib/api";
import VeggieIcon from "@/components/VeggieIcon";

const STRUCTURE_LABEL = {
    shed: "Shed", greenhouse: "Greenhouse", polytunnel: "Polytunnel",
    water_tank: "Water Tank", water_butt: "Water Butt",
    compost_bin: "Compost Bin", path: "Path", tree: "Tree", gate: "Gate",
};

export default function PublicGarden() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [scale] = useState(0.4);

    useEffect(() => {
        fetchPublicGarden(token)
            .then(setData)
            .catch((e) => setError(formatApiError(e)));
    }, [token]);

    if (error) {
        return (
            <div className="min-h-screen paper-texture flex items-center justify-center p-6" data-testid="public-garden-error">
                <div className="journal-card p-8 max-w-md text-center">
                    <Sprout className="w-10 h-10 mx-auto text-[var(--vm-sage)] mb-3" strokeWidth={1.5} />
                    <h2 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">Garden not found</h2>
                    <p className="text-sm text-[var(--vm-text-muted)]">{error}</p>
                </div>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="min-h-screen paper-texture flex items-center justify-center" data-testid="public-garden-loading">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--vm-accent)]" />
            </div>
        );
    }

    const { garden, owner, beds, structures, crops } = data;
    const cw = garden.length_cm * scale;
    const ch = garden.width_cm * scale;
    const cropsByBed = crops.reduce((acc, c) => {
        (acc[c.bed_id] ||= []).push(c);
        return acc;
    }, {});

    return (
        <div className="min-h-screen paper-texture p-6" data-testid="public-garden-page">
            <header className="max-w-5xl mx-auto mb-8">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--vm-accent)] flex items-center justify-center">
                        <Leaf className="w-5 h-5 text-[var(--vm-bg)]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="font-serif-display text-2xl text-[var(--vm-text)]">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)]">Shared Garden</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--vm-text-muted)] bg-[var(--vm-card)] inline-flex rounded-full px-3 py-1">
                    <Eye className="w-3 h-3" /> Read-only — visitors cannot edit
                </div>
            </header>

            <main className="max-w-5xl mx-auto space-y-10">
                <section className="journal-card p-8">
                    <div className="overline text-[var(--vm-sage)] mb-1">{owner?.name || "Anonymous"}'s garden</div>
                    <h1 className="font-serif-display text-4xl text-[var(--vm-text)]">{garden.name}</h1>
                    <div className="flex items-center gap-2 text-sm text-[var(--vm-text-muted)] mt-2">
                        <MapPin className="w-4 h-4" />
                        {garden.location} · {garden.climate}
                    </div>
                    {garden.notes && (
                        <p className="text-sm text-[var(--vm-text-secondary)] mt-3 italic">{garden.notes}</p>
                    )}
                </section>

                <section>
                    <div className="overline text-[var(--vm-sage)] mb-3">Garden plan</div>
                    <div
                        className="relative bg-[var(--vm-card)] border border-[var(--vm-border)] rounded-lg overflow-hidden mx-auto"
                        style={{
                            width: cw, height: ch,
                            backgroundImage: "linear-gradient(rgba(0,0,0,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,.04) 1px, transparent 1px)",
                            backgroundSize: `${50 * scale}px ${50 * scale}px`,
                        }}
                        data-testid="public-garden-canvas"
                    >
                        {beds.map((b) => (
                            <div
                                key={b.id}
                                className="absolute border-2 border-[var(--vm-accent)]/60 bg-[var(--vm-accent)]/15 rounded-md flex items-center justify-center text-center px-2"
                                style={{
                                    left: b.position_x * scale, top: b.position_y * scale,
                                    width: b.length_cm * scale, height: b.width_cm * scale,
                                }}
                                data-testid={`public-bed-${b.id}`}
                            >
                                <div>
                                    <div className="font-serif-display text-sm">{b.name}</div>
                                    <div className="text-[10px] text-[var(--vm-text-muted)] mt-0.5">
                                        {(cropsByBed[b.id] || []).length} crops
                                    </div>
                                </div>
                            </div>
                        ))}
                        {structures.map((s) => (
                            <div
                                key={s.id}
                                className="absolute border-2 border-dashed border-[var(--vm-secondary)]/50 bg-[var(--vm-secondary)]/10 rounded-md flex items-center justify-center text-center text-[10px] text-[var(--vm-text-muted)]"
                                style={{
                                    left: s.position_x * scale, top: s.position_y * scale,
                                    width: s.length_cm * scale, height: s.width_cm * scale,
                                }}
                                data-testid={`public-structure-${s.id}`}
                            >
                                {s.name || STRUCTURE_LABEL[s.type]}
                            </div>
                        ))}
                    </div>
                </section>

                <section>
                    <div className="overline text-[var(--vm-sage)] mb-3">Beds & crops</div>
                    {beds.length === 0 && (
                        <div className="text-sm text-[var(--vm-text-muted)] journal-card p-6">
                            No beds yet in this garden.
                        </div>
                    )}
                    <div className="space-y-4">
                        {beds.map((b) => (
                            <div key={b.id} className="journal-card p-5" data-testid={`public-bed-card-${b.id}`}>
                                <div className="flex items-baseline justify-between gap-3 mb-3">
                                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)]">{b.name}</h3>
                                    <span className="text-xs text-[var(--vm-text-muted)]">
                                        {b.length_cm}×{b.width_cm}cm · {b.type} · {b.sun_exposure}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(cropsByBed[b.id] || []).map((c) => (
                                        <div key={c.id} className="flex items-center gap-1.5 text-xs bg-[var(--vm-bg)] border border-[var(--vm-border)] rounded-full pl-1.5 pr-3 py-1" data-testid={`public-crop-${c.id}`}>
                                            <VeggieIcon cropName={c.crop_name} stage={c.stage} size={18} />
                                            <span className="font-medium text-[var(--vm-text)]">{c.crop_name}</span>
                                            {c.variety && <span className="text-[var(--vm-text-muted)]"> · {c.variety}</span>}
                                            <span className="text-[var(--vm-sage)] ml-1">({c.plant_count} · {c.stage})</span>
                                        </div>
                                    ))}
                                    {(cropsByBed[b.id] || []).length === 0 && (
                                        <div className="text-xs text-[var(--vm-text-muted)] italic">No crops planted yet.</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            <footer className="max-w-5xl mx-auto mt-16 pb-10 text-center text-xs text-[var(--vm-text-muted)]">
                Powered by rooteed · <a href="/" className="text-[var(--vm-accent)] underline">Start your own garden journal</a>
            </footer>
        </div>
    );
}
